import { useEffect } from 'react';
import { keyEventListeners } from '../components/shared/constants';

/**
 * Hook for handling keyboard events with object-based handlers -
 * @param {Object} keyHandlers - Object mapping key names to handler functions
 * @example
 * useKeyDown(useMemo(() => ({
 *   'ArrowUp': () => increase(),
 *   'ArrowDown': () => decrease(),
 *   'Enter': (e) => { e.preventDefault(); submit(); }
 * }), [increase, decrease, submit]));
 */

export const useKeyDown = (keyHandlers) => {

  useEffect(() => {
    const listener = (event) => {
      // Safety check - make sure we have a valid handlers object
      if (!keyHandlers || typeof keyHandlers !== 'object') return;

      // Look up handler by key name (e.g., 'ArrowUp') or key code
      const handler = keyHandlers[event.key] || keyHandlers[event.code];

      // Call the handler if it exists and is a function
      if (handler && typeof handler === 'function') {
        handler(event);
      }
    };

    // Add listener when component mounts or keyHandlers change
    window.addEventListener(keyEventListeners.keyDown, listener);

    // Remove listener when component unmounts or keyHandlers change
    return () => window.removeEventListener(keyEventListeners.keyDown, listener);
  }, [keyHandlers]); // Re-run when keyHandlers change
};
